import { z } from 'zod';
/** 공개 표준 온디맨드 단가. 과거 청구액이 아닌 이 기준일의 단가로 재계산한다. */
export const BEDROCK_COST_PRICING_DATE = '2026-11-02';
export const BEDROCK_COST_SOURCE = 'https://aws.amazon.com/bedrock/pricing/';
export const BEDROCK_COST_BASIS = '참고용 추정치이며 실제 청구액과 다를 수 있습니다. USD 공개 표준 온디맨드 단가·캐시 쓰기 5분 기준이며, 할인·세금·환율은 반영하지 않습니다.';
export const BEDROCK_COST_STATUS_LABELS = {
    estimated: '추정',
    partial: '부분 추정',
    unavailable: '계산 불가',
    not_applicable: '대상 아님',
};
export const BEDROCK_COST_REASON_LABELS = {
    not_bedrock: 'Bedrock 호출만 비용을 추정합니다. Rekognition 등 다른 공급자의 비용은 포함하지 않습니다.',
    pending: '진행 중인 호출은 비용 합계에서 제외합니다.',
    unknown_model: '등록된 공개 단가가 없는 모델입니다.',
    unknown_scope: '추론 경로의 과금 기준을 확인할 수 없습니다.',
    unknown_usage: '확인된 토큰이 없어 비용을 계산할 수 없습니다. 0원과 다릅니다.',
    partial_usage: '확인된 토큰만 합산했습니다. 미확인 사용량과 실패·중단 후 추가 사용량은 포함하지 않습니다.',
};
export const BedrockCostEstimateSchema = z.object({
    amountUsd: z.number().finite().nonnegative().nullable(),
    status: z.enum(['estimated', 'partial', 'unavailable', 'not_applicable']),
    reason: z
        .enum(['not_bedrock', 'pending', 'unknown_model', 'unknown_scope', 'unknown_usage', 'partial_usage'])
        .nullable(),
});
export const BedrockCostSummarySchema = z.object({
    amountUsd: z.number().finite().nonnegative().nullable(),
    totalCalls: z.number().int().nonnegative(),
    estimatedCalls: z.number().int().nonnegative(),
    partialCalls: z.number().int().nonnegative(),
    unavailableCalls: z.number().int().nonnegative(),
    pricingDate: z.string(),
});
const tokenCount = z.number().finite().nonnegative().nullable();
export const BedrockCostInputSchema = z.object({
    provider: z.string(),
    model: z.string(),
    responseModel: z.string().nullable(),
    status: z.enum(['pending', 'succeeded', 'failed', 'aborted']),
    usageStatus: z.enum(['measured', 'partial', 'unavailable', 'not_applicable']),
    inputTokens: tokenCount,
    outputTokens: tokenCount,
    cacheReadTokens: tokenCount,
    cacheWriteTokens: tokenCount,
});
export const BedrockCostGroupSchema = BedrockCostInputSchema.extend({ calls: z.number().int().positive() });
// USD / 100만 토큰. AWS/Anthropic 공개 단가를 확인해 명시적으로 등록한다.
// https://platform.claude.com/docs/en/about-claude/pricing
// https://docs.aws.amazon.com/bedrock/latest/userguide/prompt-caching.html
// 新モデルを既存モデルの接頭辞だけで推測しない。
const prices = [
    { model: 'claude-haiku-4-5', input: 1, output: 5, read: 0.1 },
    { model: 'claude-sonnet-4-5', input: 3, output: 15, read: 0.3 },
    { model: 'claude-sonnet-4-6', input: 3, output: 15, read: 0.3 },
    { model: 'claude-sonnet-5', input: 2, output: 10, read: 0.2 },
    { model: 'claude-sonnet-5-5', input: 2, output: 10, read: 0.1 },
    { model: 'claude-opus-4-5', input: 5, output: 25, read: 0.5 },
    { model: 'claude-opus-4-6', input: 5, output: 25, read: 0.5 },
    { model: 'claude-opus-4-7', input: 5, output: 25, read: 0.5 },
    { model: 'claude-opus-4-8', input: 5, output: 25, read: 0.5 },
    { model: 'claude-opus-5', input: 5, output: 25, read: 0.5 },
    { model: 'claude-opus-5-5', input: 4, output: 20, read: 0.2 },
];
function findPrice(model) {
    const canonical = model
        .replace(/^(?:(?:global|us|eu|apac)\.)?anthropic\./, '')
        .replace(/-\d{8}(?:-v\d+:\d+)?$/, '');
    return prices.find((price) => price.model === canonical);
}
export function estimateBedrockCost(row) {
    if (row.provider !== 'bedrock')
        return { amountUsd: null, status: 'not_applicable', reason: 'not_bedrock' };
    if (row.status === 'pending')
        return { amountUsd: null, status: 'unavailable', reason: 'pending' };
    const scope = /^(global|us|eu|apac)\.anthropic\./.exec(row.model)?.[1];
    if (!scope)
        return { amountUsd: null, status: 'unavailable', reason: 'unknown_scope' };
    // 응답 모델을 보고했다면 그 모델을 기준으로 한다. 미등록 응답 모델을 요청 모델로 대체하지 않는다.
    const price = findPrice(row.responseModel ?? row.model);
    if (!price)
        return { amountUsd: null, status: 'unavailable', reason: 'unknown_model' };
    const counts = [row.inputTokens, row.outputTokens, row.cacheReadTokens, row.cacheWriteTokens];
    if (counts.every((value) => value === null)) {
        return { amountUsd: null, status: 'unavailable', reason: 'unknown_usage' };
    }
    const partial = row.status !== 'succeeded' || row.usageStatus !== 'measured' || counts.some((value) => value === null);
    // Anthropic의 input은 비캐시 입력이며 thinking은 output에 포함돼 다시 더하지 않는다.
    const amountUsd = (((row.inputTokens ?? 0) * price.input +
        (row.outputTokens ?? 0) * price.output +
        (row.cacheReadTokens ?? 0) * price.read +
        (row.cacheWriteTokens ?? 0) * price.input * 1.25) *
        (scope === 'global' ? 1 : 1.1)) /
        1_000_000;
    return {
        amountUsd,
        status: partial ? 'partial' : 'estimated',
        reason: partial ? 'partial_usage' : null,
    };
}
/** null 여부·모델·상태가 같은 그룹의 토큰 합계다. 금액에 건수를 다시 곱하지 않는다. */
export function summarizeBedrockCosts(groups) {
    const summary = {
        amountUsd: null,
        totalCalls: 0,
        estimatedCalls: 0,
        partialCalls: 0,
        unavailableCalls: 0,
        pricingDate: BEDROCK_COST_PRICING_DATE,
    };
    for (const group of groups) {
        if (group.provider !== 'bedrock')
            continue;
        const cost = estimateBedrockCost(group);
        summary.totalCalls += group.calls;
        if (cost.status === 'estimated')
            summary.estimatedCalls += group.calls;
        else if (cost.status === 'partial')
            summary.partialCalls += group.calls;
        else
            summary.unavailableCalls += group.calls;
        if (cost.amountUsd !== null)
            summary.amountUsd = (summary.amountUsd ?? 0) + cost.amountUsd;
    }
    return summary;
}
//# sourceMappingURL=ai-usage-cost.js.map